"use client";

import { ExternalLink, Truck } from "lucide-react";
import { trackIntent } from "../../../_actions/trackIntent";

interface AlternativeCarrier {
  carrier_id: string;
  name: string;
  city: string;
  slug: string;
  city_slug: string;
  description: string | null;
  fleet_size: number | null;
}

interface CitySuggestion {
  city: string;
  city_slug: string;
}

interface AlternativeCarriersListProps {
  result:
    | {
        alternatives: AlternativeCarrier[];
        searchedLocation?: string;
        suggestions?: CitySuggestion[];
      }
    | null;
  state: string;
  sessionId: string;
  onSendMessage?: (text: string) => void;
}

export function AlternativeCarriersList({
  result,
  state,
  sessionId,
  onSendMessage,
}: AlternativeCarriersListProps) {
  if (state === "partial-call" || state === "input-streaming" || state === "call") {
    return (
      <div className="space-y-2 animate-pulse my-2">
        {[1, 2].map((i) => (
          <div key={i} className="h-16 bg-gray-100 rounded-lg" />
        ))}
      </div>
    );
  }

  // No carriers matched — show an empty state with other cities to try, so the
  // search never dead-ends silently.
  if (!result?.alternatives?.length) {
    const suggestions = result?.suggestions ?? [];
    if (!suggestions.length) return null;

    const searched = result?.searchedLocation?.trim();
    return (
      <div className="border border-gray-200 rounded-lg p-3 bg-white my-2">
        <p className="text-sm text-gray-900">
          {searched
            ? `No encontré transportistas en ${searched} por ahora.`
            : "No encontré transportistas ahí por ahora."}
        </p>
        <p className="text-xs text-gray-500 mt-1 mb-2">Prueba en otra ciudad:</p>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button
              key={s.city_slug}
              onClick={() => onSendMessage?.(`Busca transportistas en ${s.city}`)}
              className="text-xs border border-gray-200 rounded-full px-3 py-1 bg-white hover:border-primary hover:bg-gray-50 transition-colors cursor-pointer"
            >
              {s.city}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const handleRequestContact = (carrier: AlternativeCarrier) => {
    // Best-effort telemetry. Catch the rejection so a stale or non-RSC server
    // action response cannot escape as an unhandled promise.
    trackIntent({ sessionId, carrierId: carrier.carrier_id, intentType: "alternative_clicked" }).catch(() => {});
    // carrier_id needed for the LLM to call getCarrierContactTool
    onSendMessage?.(`[carrier:${carrier.carrier_id}] Busca los datos de contacto de ${carrier.name}`);
  };

  return (
    <div className="space-y-2 my-2">
      {result.alternatives.map((carrier) => {
        const profileUrl = `/fletes/${carrier.city_slug}/transportista/${carrier.slug}`;

        return (
          <button
            key={carrier.carrier_id}
            onClick={() => handleRequestContact(carrier)}
            className="w-full text-left border border-gray-200 rounded-lg p-3 bg-white hover:border-primary hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm text-gray-900 truncate">
                  {carrier.name}
                </p>
                <p className="text-xs text-gray-500">{carrier.city}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                {carrier.fleet_size != null && carrier.fleet_size > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    <Truck className="h-3 w-3" />
                    {carrier.fleet_size}
                  </span>
                )}
                <a
                  href={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  Perfil
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
            {carrier.description && (
              <p className="text-xs text-gray-500 line-clamp-2">{carrier.description}</p>
            )}
            <p className="text-xs text-primary mt-1 font-medium">Toca para solicitar datos de contacto</p>
          </button>
        );
      })}
    </div>
  );
}
